import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useSimulator } from '../../../../contexts/simulator';
import { parseSpectrumSetFromIot } from '../../../../utils/fft-spectrum-parse';
import { generateSpectrumSet } from '../../../../services/fft-simulator';
import FftSpectrum from '../../../../components/fft-spectrum/fft-spectrum';

// Thin container: it only SOURCES the spectrum (real IoT snapshot, unchanged
// reading path) and hands a normalized payload to <FftSpectrum>. Real data
// always wins; the simulator is a developer-only fallback used to preview the
// layout when the Bluebox has not streamed an FFT yet.

// Fault profiles cycled by the simulator — each activation advances `cycle`
// and shows the next one, so devs can eyeball every spectrum shape.
const PROFILE_CYCLE = [
  'healthy',
  'unbalance',
  'misalignment',
  'bearing',
  'cavitation',
];

const AXES = ['x', 'y', 'z'];

const BlueboxFftChart = ({ thing, axis = 'Z' }) => {
  const thingId = thing && thing.id;
  const iotData = useSelector((state) =>
    thingId ? state.aplicacao.velocimetro[thingId] : null
  );
  const { enabled: simulatorEnabled, cycle } = useSimulator();
  // Toggles live here so they persist while the user switches FFTX/Y/Z tabs
  // (same component instance, only the `axis` prop changes).
  const [scaleType, setScaleType] = useState('linear');
  const [locked, setLocked] = useState(false);

  // All three axes come from the same snapshot; parsing the set lets the
  // locked scale compare them. Real first, simulator only as fallback.
  const spectra = useMemo(() => {
    const real = parseSpectrumSetFromIot(iotData);
    if (real.x || real.y || real.z) return real;
    if (simulatorEnabled) {
      const profile = PROFILE_CYCLE[cycle % PROFILE_CYCLE.length];
      return generateSpectrumSet({ profile, sampleIndex: cycle });
    }
    return { x: null, y: null, z: null };
  }, [iotData, simulatorEnabled, cycle]);

  const active = spectra[String(axis).toLowerCase()] || null;

  // Shared Y max across the three axes, so "escala travada" makes them
  // directly comparable. Bin 0 (DC) excluded.
  const lockedMax = useMemo(() => {
    if (!locked) return null;
    let max = 0;
    AXES.forEach((key) => {
      const sp = spectra[key];
      if (sp && Array.isArray(sp.magnitudes)) {
        for (let i = 1; i < sp.magnitudes.length; i += 1) {
          if (sp.magnitudes[i] > max) max = sp.magnitudes[i];
        }
      }
    });
    return max > 0 ? max : null;
  }, [locked, spectra]);

  return (
    <FftSpectrum
      spectrum={active}
      scaleType={scaleType}
      onScaleChange={setScaleType}
      locked={locked}
      lockedMax={lockedMax}
      onLockChange={setLocked}
    />
  );
};

export default BlueboxFftChart;
